import { type FormEvent, useRef } from "react";
import { CONTACT_EMAIL, CONTACT_EMAIL_IS_PLACEHOLDER } from "../config";

type ContactFields = {
  name: string;
  email: string;
  company: string;
  facility: string;
  discharge: string;
  decision: string;
  spend: string;
};

function buildMailto(fields: ContactFields) {
  const subject = `Kratis Cost Increase Review — ${fields.company}`;
  const body = [
    `Name: ${fields.name}`,
    `Work email: ${fields.email}`,
    `Company: ${fields.company}`,
    `Facility or industry: ${fields.facility}`,
    `Discharges to a municipal sewer: ${fields.discharge}`,
    `Approximate annual spend or amount at risk: ${fields.spend || "Not provided"}`,
    "",
    "Wastewater cost change:",
    fields.decision,
  ].join("\n");

  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

export function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = formRef.current;
    if (!form) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const data = new FormData(form);
    const mailto = buildMailto({
      name: String(data.get("name") ?? ""),
      email: String(data.get("email") ?? ""),
      company: String(data.get("company") ?? ""),
      facility: String(data.get("facility") ?? ""),
      discharge: String(data.get("discharge") ?? ""),
      decision: String(data.get("decision") ?? ""),
      spend: String(data.get("spend") ?? ""),
    });

    const mailLink = document.createElement("a");
    mailLink.href = mailto;
    mailLink.rel = "noreferrer";
    mailLink.click();
  };

  return (
    <form
      ref={formRef}
      className="contact-form"
      onSubmit={handleSubmit}
      data-contact-email={CONTACT_EMAIL}
    >
      <div className="form-row">
        <label>
          <span>Name</span>
          <input
            type="text"
            name="name"
            autoComplete="name"
            required
            maxLength={100}
            placeholder="Your name"
          />
        </label>
        <label>
          <span>Work email</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            maxLength={254}
            placeholder="you@company.com"
          />
        </label>
      </div>
      <div className="form-row">
        <label>
          <span>Company</span>
          <input
            type="text"
            name="company"
            autoComplete="organization"
            required
            maxLength={120}
            placeholder="Company name"
          />
        </label>
        <label>
          <span>Facility or industry</span>
          <input
            type="text"
            name="facility"
            autoComplete="off"
            required
            maxLength={120}
            placeholder="e.g. food manufacturing"
          />
        </label>
      </div>
      <label>
        <span>What wastewater cost changed?</span>
        <textarea
          name="decision"
          rows={5}
          required
          maxLength={1600}
          placeholder="When did it increase, by about how much, and has anyone proposed a cause or fix?"
        />
      </label>
      <div className="form-row">
        <label>
          <span>Does this facility discharge to a municipal sewer?</span>
          <select name="discharge" defaultValue="" required>
            <option value="" disabled>
              Choose one
            </option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Not sure">Not sure</option>
          </select>
        </label>
        <label>
          <span>
            Annual spend or amount at risk <small>(optional)</small>
          </span>
          <select name="spend" defaultValue="">
            <option value="">Choose a range</option>
            <option value="Under $100,000">Under $100,000</option>
            <option value="$100,000–$500,000">$100,000–$500,000</option>
            <option value="$500,000–$1 million">$500,000–$1 million</option>
            <option value="$1 million–$5 million">$1 million–$5 million</option>
            <option value="More than $5 million">More than $5 million</option>
            <option value="Not sure">Not sure</option>
          </select>
        </label>
      </div>
      <button className="button contact-form__submit" type="submit">
        Request a Cost Increase Review
        <span aria-hidden="true">↗</span>
      </button>
      <p className="form-note">
        Do not send facility records through this public form. Kratis will
        arrange a controlled method for record sharing after the initial
        conversation.
      </p>
      {CONTACT_EMAIL_IS_PLACEHOLDER ? (
        <p className="development-note" role="note">
          <strong>Development contact:</strong> submitting currently opens an
          email to <code>{CONTACT_EMAIL}</code>. Replace this address before
          public launch.
        </p>
      ) : (
        <p className="form-note">
          Submitting opens a prepared email draft. You can also write directly
          to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      )}
    </form>
  );
}
